import MuiPortal from '@mui/material/Portal';
import MuiSnackbar from '@mui/material/Snackbar';
import MuiStack from '@mui/material/Stack';
import { Alert } from '../Alert';

export interface SnackbarNotice {
  id: string;
  severity: 'error' | 'warning' | 'info' | 'success';
  message: string;
}

export interface SnackbarProps {
  // A LIST and not a message, because MUI does not stack independent `Snackbar`s: two of them mount
  // at the same `anchorOrigin`, overlapping, and the second failure becomes unreadable. Since these
  // notices survive until dismissed, two at once is a real case — so one instance renders all of
  // them and the host keeps a queue instead of mounting a second component.
  notices: readonly SnackbarNotice[];
  // Called by a notice's «X» with its id — the only way it goes away. Deliberately NOT wired to
  // MUI's own `onClose`, which also fires on a click away and on Escape.
  onDismiss: (id: string) => void;
  // Two named positions rather than MUI's `anchorOrigin`: the panel has one notice pattern, not a
  // positioning system. The default floats over the header, where the pattern was designed.
  anchor?: 'top-right' | 'top-center';
}

const ANCHOR_ORIGIN = {
  'top-right': { vertical: 'top', horizontal: 'right' },
  'top-center': { vertical: 'top', horizontal: 'center' },
} as const;

// The panel's floating notice (ADR 0021). Three facts are enforced HERE and not at the call sites:
// no `autoHideDuration` — an error on a timer fails WCAG 2.2.1 and is unreadable to whoever was
// looking elsewhere — the stacking above, and the portal below. Semantic, so it exposes no `sx`.
export function Snackbar({ notices, onDismiss, anchor = 'top-center' }: SnackbarProps) {
  // Nothing at all rather than an `open={false}` container: MUI keeps the positioned root mounted,
  // and an empty one sits over the header's own controls and swallows clicks aimed at them.
  if (notices.length === 0) return null;
  return (
    // AT THE END OF THE BODY, which MUI's own `Snackbar` does not do (it renders in place, merely
    // `position: fixed`). Two reasons, and the first is accessibility rather than layout: an open
    // MUI modal marks every body-level sibling `aria-hidden`, so a notice nested in the app's own
    // container is invisible to a screen reader while any dialog is up — as its own node, created
    // after the modal's sweep, it is not. The second is that `fixed` inside a transformed or
    // clipped ancestor is measured against that ancestor, not the viewport.
    <MuiPortal>
      <MuiSnackbar open anchorOrigin={ANCHOR_ORIGIN[anchor]}>
        {/* MUI's transition mounts ONE child and reads layout off its node, so the stack is what it
            animates — hence MUI's own `Stack`, which forwards a ref, and not the façade's. Notices
            render in arrival order, oldest first: appending at the bottom leaves the text already
            on screen where the agent was reading it. */}
        <MuiStack spacing={1}>
          {notices.map((notice) => (
            <Alert
              key={notice.id}
              variant="filled"
              severity={notice.severity}
              onClose={() => {
                onDismiss(notice.id);
              }}
            >
              {notice.message}
            </Alert>
          ))}
        </MuiStack>
      </MuiSnackbar>
    </MuiPortal>
  );
}
